import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Bell, Eye, CheckCircle, XCircle, Mail, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useWebSocket } from '../hooks/useWebSocket';
import { formatDistanceToNow } from '../lib/utils';

const NOTIFICATION_ICONS = {
  presupuesto_visto: <Eye className="w-4 h-4 text-blue-500" />,
  presupuesto_aprobado: <CheckCircle className="w-4 h-4 text-green-500" />,
  presupuesto_rechazado: <XCircle className="w-4 h-4 text-red-500" />,
  email_abierto: <Mail className="w-4 h-4 text-purple-500" />,
};

const NOTIFICATION_COLORS = {
  presupuesto_visto: 'border-l-blue-500',
  presupuesto_aprobado: 'border-l-green-500',
  presupuesto_rechazado: 'border-l-red-500',
  email_abierto: 'border-l-purple-500',
};

export function RealtimeNotifications({ token }) {
  const navigate = useNavigate();
  const { 
    isConnected, 
    lastMessage, 
    notifications, 
    unreadCount, 
    markAsRead,
    clearNotifications 
  } = useWebSocket(token);

  // Show toast for new notifications
  useEffect(() => {
    if (lastMessage?.type === 'notification') {
      const notification = lastMessage.notification;
      
      // Show toast based on type
      if (notification.tipo === 'presupuesto_aprobado') {
        toast.success(notification.titulo, {
          description: notification.mensaje,
          action: {
            label: 'Ver',
            onClick: () => navigate(`/presupuestos/${notification.presupuesto_id}`)
          }
        });
      } else if (notification.tipo === 'presupuesto_rechazado') {
        toast.error(notification.titulo, {
          description: notification.mensaje,
          action: {
            label: 'Ver',
            onClick: () => navigate(`/presupuestos/${notification.presupuesto_id}`)
          }
        });
      } else {
        toast(notification.titulo, {
          description: notification.mensaje,
          action: notification.presupuesto_id ? {
            label: 'Ver',
            onClick: () => navigate(`/presupuestos/${notification.presupuesto_id}`)
          } : undefined
        });
      }
    }
  }, [lastMessage, navigate]);

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    if (notification.presupuesto_id) {
      navigate(`/presupuestos/${notification.presupuesto_id}`);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notifications-bell"
        >
          <Bell className={`w-5 h-5 ${isConnected ? 'text-slate-600' : 'text-slate-400'}`} />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
          {isConnected && (
            <span className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full border border-white" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notificaciones</span>
          {notifications.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 text-xs"
              onClick={(e) => {
                e.preventDefault();
                clearNotifications();
              }}
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Limpiar
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {notifications.length === 0 ? (
          <div className="py-8 text-center text-slate-500">
            <Bell className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm">Sin notificaciones</p>
            <p className="text-xs mt-1">
              {isConnected ? 'Recibirás alertas aquí' : 'Conectando...'}
            </p>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {notifications.slice(0, 10).map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`flex items-start gap-3 p-3 cursor-pointer border-l-4 ${
                  NOTIFICATION_COLORS[notification.tipo] || 'border-l-slate-300'
                } ${notification.leida ? 'opacity-60' : 'bg-blue-50/50'}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="mt-0.5">
                  {NOTIFICATION_ICONS[notification.tipo] || <Bell className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {notification.titulo}
                  </p>
                  <p className="text-xs text-slate-600 line-clamp-2">
                    {notification.mensaje}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {formatDistanceToNow(notification.timestamp)}
                  </p>
                </div>
                {!notification.leida && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                )}
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
